import { api } from './api';
import { SearchEngine } from '../store/engine.store';
import { Site } from '../store/site.store';

// Backend DTO (what API returns)
interface SiteApiResponse {
  id: number;
  tenantId: number;
  name: string;
  siteUrl: string;
  propertyType?: 'DOMAIN' | 'URL_PREFIX';
  timezone?: string;
  isActive?: boolean;
  configuration?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  mode?: string;
  businessType?: string;
  customerEmail?: string;
  customerName?: string;
  customerId?: string;
  gscPropertyId?: string;
  bingId?: string;
  ga4PropertyId?: string;
  pinterestId?: string;
  agencyCode?: string;
}

export interface AgencyPublicInfo {
  code: string;
  name: string;
  website?: string;
  contactEmail?: string;
  contactPhone?: string;
  status: string;
  logoUrl?: string;
}

export interface GscMetrics {
  url: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  date: string;
  engine?: SearchEngine; // Added for multi-engine support
}

export interface MetricsQueryParams {
  siteId: number;
  startDate: string;
  endDate: string;
  dimension?: 'page' | 'query' | 'device' | 'country';
  limit?: number;
  engine?: SearchEngine; // Added for engine filtering
}

export interface TimeSeriesData {
  date: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  engine?: SearchEngine; // Added for multi-engine support
}

// Backend API response wrapper
interface ApiResponse<T> {
  data: T;
  success: boolean;
  requestId: string;
  timestamp: number;
}

const normalizeSiteMode = (mode?: string): string | undefined => {
  if (!mode) {
    return mode;
  }

  const normalized = mode.trim().replace(/-/g, '_').toUpperCase();
  if (normalized === 'EARLY_STAGE') {
    return 'EARLY_STAGE';
  }
  if (normalized === 'STANDARD') {
    return 'PERFORMANCE';
  }
  return normalized;
};

export const metricsService = {
  // Fetch sites - maps backend response to frontend Site interface
  async getSites(): Promise<Site[]> {
    const response = await api.get<ApiResponse<SiteApiResponse[]>>('/customer/sites');
    console.log('🔍 Raw API response from /sites:', response.data.data);
    
    // Map backend fields to frontend Site interface
    return response.data.data.map((site): Site => ({
      id: site.id,
      name: site.name,
      url: site.siteUrl,
      propertyType: site.propertyType,
      organizationId: site.tenantId,
      createdAt: site.createdAt,
      updatedAt: site.updatedAt,
      timezone: site.timezone,
      isActive: site.isActive,
      configuration: site.configuration,
      mode: normalizeSiteMode(site.mode),
      businessType: site.businessType,
      customerEmail: site.customerEmail,
      customerName: site.customerName,
      customerId: site.customerId,
      gscPropertyId: site.gscPropertyId,
      bingId: site.bingId,
      ga4PropertyId: site.ga4PropertyId,
      pinterestId: site.pinterestId,
      agencyCode: site.agencyCode,
    }));
  },

  // Fetch site by ID
  async getSite(siteId: number): Promise<Site> {
    const response = await api.get<ApiResponse<SiteApiResponse>>(`/sites/${siteId}`);
    const site = response.data.data;
    return {
      id: site.id,
      name: site.name,
      url: site.siteUrl,
      propertyType: site.propertyType,
      organizationId: site.tenantId,
      createdAt: site.createdAt,
      updatedAt: site.updatedAt,
      timezone: site.timezone,
      isActive: site.isActive,
      configuration: site.configuration,
      mode: normalizeSiteMode(site.mode),
      businessType: site.businessType,
      customerEmail: site.customerEmail,
      customerName: site.customerName,
      customerId: site.customerId,
      gscPropertyId: site.gscPropertyId,
      bingId: site.bingId,
      ga4PropertyId: site.ga4PropertyId,
      pinterestId: site.pinterestId,
      agencyCode: site.agencyCode,
    };
  },

  // Fetch public agency info (for customers linked to an agency)
  async getAgencyPublicInfo(agencyCode: string): Promise<AgencyPublicInfo> {
    const response = await api.get<{ data: AgencyPublicInfo; success: boolean }>(
      `/oauth/customer/agency/${encodeURIComponent(agencyCode)}`
    );
    return response.data.data;
  },

  // Fetch GSC metrics (now supports engine filtering)
  async getMetrics(params: MetricsQueryParams): Promise<GscMetrics[]> {
    const response = await api.get('/gsc/metrics', { params });
    return response.data;
  },

  // Fetch time-series data for charts (now supports engine filtering)
  async getTimeSeriesMetrics(
    siteId: number,
    startDate: string,
    endDate: string,
    engine?: SearchEngine
  ): Promise<TimeSeriesData[]> {
    const response = await api.get(`/sites/${siteId}/metrics/timeseries`, {
      params: { startDate, endDate, engine },
    });
    return response.data;
  },

  // Fetch top pages (now supports engine filtering)
  async getTopPages(
    siteId: number,
    startDate: string,
    endDate: string,
    limit = 10,
    engine?: SearchEngine
  ): Promise<GscMetrics[]> {
    const response = await api.get(`/sites/${siteId}/metrics/top-pages`, {
      params: { startDate, endDate, limit, engine },
    });
    return response.data;
  },

  // Fetch top queries (now supports engine filtering)
  async getTopQueries(
    siteId: number,
    startDate: string,
    endDate: string,
    limit = 10,
    engine?: SearchEngine
  ): Promise<GscMetrics[]> {
    const response = await api.get(`/sites/${siteId}/metrics/top-queries`, {
      params: { startDate, endDate, limit, engine },
    });
    return response.data;
  },
};
